/* Lumetier theme applier — turns a resolved hex palette into the shadcn-style
   HSL CSS variables that Tailwind reads (--background, --foreground, --primary,
   --primary-foreground, --muted, --accent, --border, --card, --ring,
   --muted-foreground, --secondary, --gold). Also derives sensible border /
   muted-foreground / card values from the palette anchors so the studio owner
   only sets 6 colors and the rest stays harmonious.

   Two modes:
     - applyTheme(palette, target)         — push variables onto an element (usually
                                              document.documentElement or a scoped div).
     - paletteToCssVars(palette)           — pure object {var: hslString} for inline
                                              React style. */

import { resolvePalette } from "./themes";

/* hex (#RGB or #RRGGBB) → "h s% l%" string for CSS `hsl(var(--x))` consumption.
   Returns null when the input isn't a valid hex color. */
export function hexToHslTriplet(hex) {
  if (!hex || typeof hex !== "string") return null;
  let h = hex.trim().replace(/^#/, "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (!/^[0-9a-f]{6}$/i.test(h)) return null;
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let hh = 0;
  let s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: hh = ((g - b) / d + (g < b ? 6 : 0)); break;
      case g: hh = ((b - r) / d + 2); break;
      default: hh = ((r - g) / d + 4); break;
    }
    hh *= 60;
  }
  return `${Math.round(hh)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/* Given an HSL triplet "h s% l%", nudge the lightness by `delta` percentage
   points (clamped 0..100). Used to derive border / muted-foreground / card from
   the anchor colors so themes stay self-consistent without extra inputs. */
function shiftL(triplet, delta) {
  if (!triplet) return triplet;
  const m = triplet.match(/^(\d+)\s+(\d+)%\s+(\d+)%$/);
  if (!m) return triplet;
  const h = m[1];
  const s = m[2];
  const l = Math.min(100, Math.max(0, parseInt(m[3], 10) + delta));
  return `${h} ${s}% ${l}%`;
}

/* Derived CSS variables from the 6 anchor colors. We keep the palette
   surface-area small for end-users but emit the full shadcn variable set so
   every component (cards, borders, inputs, rings, etc.) stays themed. */
export function paletteToCssVars(palette) {
  const bg = hexToHslTriplet(palette.background);
  const fg = hexToHslTriplet(palette.foreground);
  const primary = hexToHslTriplet(palette.primary);
  const primaryFg = hexToHslTriplet(palette.primary_foreground);
  const accent = hexToHslTriplet(palette.accent);
  const muted = hexToHslTriplet(palette.muted);

  if (!bg || !fg || !primary || !primaryFg || !accent || !muted) {
    return {}; // bad hex somewhere — bail and let defaults win
  }

  // Determine if the theme is overall dark (bg lightness < 25%) — flips how we
  // derive borders/card/muted-foreground so dark themes don't get washed out.
  const bgL = parseInt(bg.match(/(\d+)%$/)[1], 10);
  const dark = bgL < 25;

  // shadcn `--accent` is used for SUBTLE UI affordances (dropdown hover, card
  // hover, secondary surfaces) — distinct from `--gold` which paints the
  // editorial italic/eyebrow accent. By default we soften the palette's accent
  // hue so it works for shadcn hover use. A preset can opt out by setting an
  // explicit `ui_accent` hex (editorial does this to keep Allure pixel-
  // identical with its long-standing beige `#D4C5B9` hover).
  const uiAccentHex = palette.ui_accent;
  const uiAccent = uiAccentHex ? hexToHslTriplet(uiAccentHex) : (dark ? shiftL(accent, -30) : shiftL(accent, +20));

  return {
    "--background": bg,
    "--foreground": fg,
    "--card": dark ? shiftL(bg, +4) : bg,
    "--card-foreground": fg,
    "--popover": dark ? shiftL(bg, +4) : bg,
    "--popover-foreground": fg,
    "--primary": primary,
    "--primary-foreground": primaryFg,
    "--secondary": muted,
    "--secondary-foreground": fg,
    "--muted": muted,
    "--muted-foreground": dark ? shiftL(fg, -25) : shiftL(fg, +35),
    "--accent": uiAccent || accent,
    "--accent-foreground": fg,
    "--border": dark ? shiftL(bg, +10) : shiftL(bg, -8),
    "--input": dark ? shiftL(bg, +10) : shiftL(bg, -8),
    "--ring": accent,
    // Gold accent (eyebrows, italic accents, dividers) maps to the theme's
    // accent color so existing .eyebrow, .gold-rule, .accent-italic classes
    // automatically respect the theme.
    "--gold": accent,
    "--gold-soft": shiftL(accent, +30),
  };
}

/* Push the CSS variables onto a target element. Returns a cleanup function that
   restores the previous values, so a page can scope its theme cleanly. */
export function applyTheme({ themeId, overrides = {}, target } = {}) {
  if (typeof document === "undefined") return () => {};
  const el = target || document.documentElement;
  const palette = resolvePalette(themeId, overrides);
  const vars = paletteToCssVars(palette);

  // Capture previous values so we can roll back on unmount.
  const previous = {};
  for (const key of Object.keys(vars)) {
    previous[key] = el.style.getPropertyValue(key);
    el.style.setProperty(key, vars[key]);
  }

  return () => {
    for (const key of Object.keys(vars)) {
      if (previous[key]) el.style.setProperty(key, previous[key]);
      else el.style.removeProperty(key);
    }
  };
}
